import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaBell, FaChartLine, FaHome, FaMapMarkedAlt, FaCheckCircle } from 'react-icons/fa';

import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { computeBranding } from '../utils/useAgentDetails';

function TrackPropertyModal({ show, onHide, agentDetails, propertyId, propertyAddress, agentSlug }) {
  const [step, setStep] = useState('intro');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setStep('intro');
    setName(''); setEmail(''); setMobile('');
    setIsSubmitting(false); setMessage(''); setSubmitted(false);
  };
  const handleClose = () => { reset(); onHide(); };

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  // AU mobile: starts with 04 (10 digits) or +614 / 614 (11–12 digits)
  const validateAuMobile = (m) => {
    const digits = m.replace(/[^\d]/g, '');
    if (/^04\d{8}$/.test(digits)) return true;
    if (/^614\d{8}$/.test(digits)) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    const cleanMobile = mobile.trim();
    if (!name.trim()) { setMessage('Please enter your name.'); return; }
    if (!cleanEmail && !cleanMobile) {
      setMessage('Please add an email or mobile so we can send your alerts.');
      return;
    }
    if (cleanEmail && !validateEmail(cleanEmail)) {
      setMessage('Please enter a valid email address.');
      return;
    }
    if (cleanMobile && !validateAuMobile(cleanMobile)) {
      setMessage('Please enter a valid Australian mobile (e.g. 0412 345 678).');
      return;
    }

    trackEvent('submit_track_property_public', { property_id: propertyId });
    setIsSubmitting(true);
    setMessage('');
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/track-property-public`,
        {
          name: name.trim(),
          email: cleanEmail,
          mobile: cleanMobile,
          property_value: propertyId,
          property_label: propertyAddress || `Property ${propertyId}`,
          agent_slug: agentSlug,
        },
        { 'Content-Type': 'application/json' }
      );
      setSubmitted(true);
    } catch (err) {
      console.error('track-property-public error:', err);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { accentColor, buttonTextColor, isLight } = computeBranding(agentDetails) || {};

  const inputStyle = {
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: '11px 14px',
    fontSize: '0.88rem',
  };

  const benefits = [
    { Icon: FaChartLine,    title: 'Price moves on this property' },
    { Icon: FaMapMarkedAlt, title: 'Activity around you' },
    { Icon: FaBell,         title: 'New comparable sales & rentals' },
  ];

  return (
    <Modal show={show} onHide={handleClose} centered fullscreen="sm-down" style={{ zIndex: 9999 }}>
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        {step === 'intro' && !submitted ? null : (
          <Modal.Title style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>
            Track this property
          </Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body style={{ overflowY: 'auto', padding: step === 'intro' && !submitted ? '0 24px 8px' : '8px 24px 16px', display: step === 'intro' && !submitted ? 'flex' : 'block', flexDirection: 'column', justifyContent: 'center' }}>
        {step === 'intro' && !submitted ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: `${accentColor || '#0a9fa6'}15`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 18,
            }}>
              <FaChartLine size={28} color={accentColor || '#0a9fa6'} />
            </div>
            <h4 style={{ fontWeight: 800, color: '#111', fontSize: '1.35rem', letterSpacing: '-0.02em', lineHeight: 1.25, marginBottom: 10 }}>
              Know what your home is worth — every month.
            </h4>
            <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.5, margin: '0 auto 20px', maxWidth: 320 }}>
              See what's sold and rented nearby, plus a fresh estimate on your property.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320, margin: '0 auto' }}>
              {[
                { Icon: FaHome,         text: 'Updated property value' },
                { Icon: FaMapMarkedAlt, text: 'Nearby sales & rentals' },
                { Icon: FaBell,         text: 'One email a month' },
              ].map(({ Icon, text }, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9fafb', borderRadius: 10, fontSize: '0.88rem', fontWeight: 600, color: '#111' }}>
                  <Icon size={14} color={accentColor || '#0a9fa6'} />
                  {text}
                </div>
              ))}
            </div>
          </div>
        ) : submitted ? (
          <div style={{ textAlign: 'center', padding: '24px 8px' }}>
            <FaCheckCircle size={44} color={accentColor || '#10b981'} style={{ marginBottom: 12 }} />
            <h5 style={{ fontWeight: 700, color: '#111', marginBottom: 8 }}>You're tracking this property</h5>
            <p style={{ color: '#6b7280', fontSize: '0.92rem', lineHeight: 1.55, margin: 0 }}>
              We'll send updates to {email || mobile} as soon as anything changes on{propertyAddress ? ` ${propertyAddress}` : ' this property'}.
            </p>
          </div>
        ) : (
          <>
            <p style={{ marginBottom: 14, fontSize: '0.92rem', color: '#374151', lineHeight: 1.55 }}>
              Stay one step ahead on{propertyAddress ? <strong> {propertyAddress}</strong> : ' this property'}.
            </p>

            <div style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 12, padding: '10px 14px', marginBottom: 16 }}>
              {benefits.map(({ Icon, title }, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '7px 0', borderBottom: i < benefits.length - 1 ? '1px solid #f1f1f1' : 'none' }}>
                  <Icon size={15} color={accentColor || '#0a9fa6'} style={{ flexShrink: 0 }} />
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#111' }}>{title}</div>
                </div>
              ))}
            </div>

            <Form onSubmit={handleSubmit}>
              <Form.Label style={{ fontSize: '0.78rem', marginBottom: 4, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ ...inputStyle, marginBottom: 10 }}
              />

              <Form.Label style={{ fontSize: '0.78rem', marginBottom: 4, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ ...inputStyle, marginBottom: 10 }}
              />

              <Form.Label style={{ fontSize: '0.78rem', marginBottom: 4, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Mobile</Form.Label>
              <Form.Control
                type="tel"
                placeholder="0412 345 678"
                inputMode="tel"
                pattern="[0-9 +]*"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                style={{ ...inputStyle, marginBottom: 6 }}
              />
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 12 }}>
                Add email, mobile, or both — whichever way you'd like the alerts.
              </div>

              {message && (
                <Alert variant="secondary" className="mt-2" style={{ borderRadius: 12, border: 'none', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.85rem', textAlign: 'center' }}>
                  {message}
                </Alert>
              )}
            </Form>
          </>
        )}
      </Modal.Body>
      {step === 'intro' && !submitted && (
        <Modal.Footer style={{ border: 'none', paddingTop: 4, paddingBottom: 20, paddingLeft: 24, paddingRight: 24, flexDirection: 'column', gap: 8 }}>
          <Button
            onClick={() => { trackEvent('track_property_public_intro_yes', { property_id: propertyId }); setStep('form'); }}
            className="w-100"
            style={{
              backgroundColor: accentColor || '#0a9fa6',
              borderColor: isLight ? '#222' : (accentColor || '#0a9fa6'),
              color: buttonTextColor || '#fff',
              borderRadius: 12,
              padding: '13px 0',
              fontWeight: 700,
              fontSize: '0.98rem',
              boxShadow: `0 4px 16px ${(accentColor || '#0a9fa6')}30`,
              marginLeft: 0,
            }}
          >
            Yes, send me updates
          </Button>
          <Button
            onClick={() => { trackEvent('track_property_public_intro_no', { property_id: propertyId }); handleClose(); }}
            variant="link"
            className="w-100"
            style={{
              color: '#6b7280',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              padding: '8px 0',
              marginLeft: 0,
            }}
          >
            No thanks
          </Button>
        </Modal.Footer>
      )}
      {step === 'form' && !submitted && (
        <Modal.Footer style={{ border: 'none', paddingTop: 4, paddingBottom: 20, paddingLeft: 24, paddingRight: 24, flexDirection: 'column', gap: 8 }}>
          <Button
            onClick={handleSubmit}
            className="w-100"
            disabled={isSubmitting}
            style={{
              backgroundColor: accentColor || '#0a9fa6',
              borderColor: isLight ? '#222' : (accentColor || '#0a9fa6'),
              color: buttonTextColor || '#fff',
              borderRadius: 12,
              padding: '13px 0',
              fontWeight: 700,
              fontSize: '0.98rem',
              boxShadow: `0 4px 16px ${(accentColor || '#0a9fa6')}30`,
              marginLeft: 0,
            }}
          >
            {isSubmitting ? <Spinner animation="border" size="sm" /> : <><FaBell style={{ marginRight: 8, marginBottom: 2 }} /> Track Property</>}
          </Button>
          <Button
            onClick={handleClose}
            variant="link"
            className="w-100"
            disabled={isSubmitting}
            style={{
              color: '#6b7280',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              padding: '8px 0',
              marginLeft: 0,
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default TrackPropertyModal;
